import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { Grid } from '@/components/Grid';
import { LegacyButton } from '@/components/LegacyButton';
import { Typography } from '@/components/Typography';
import { DEFAULT_MOBILE_HEADER_HEIGHT } from '@/theme/dimensions';
import { scrollbar } from '@/theme/scrollbar';

import { ReactComponent as CloseSvg } from '@/assets/icons/close-icon.svg';

const topMenuHeight = `${DEFAULT_MOBILE_HEADER_HEIGHT}px`;
export const Wrapper = styled.aside<{
  $hasHeader?: boolean;
  $side: 'left' | 'right';
  $top?: number;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100dvh;

  ${({ $hasHeader }) =>
    $hasHeader &&
    css`
      height: calc(100dvh - ${topMenuHeight});
    `};

  top: ${({ $top }) => ($top ? `${$top}px` : topMenuHeight)};

  ${({
    $side,
    theme: {
      colors: { shadow2, white },
      components: { addToCartDialog },
      media,
    },
  }) => css`
    background-color: ${white};
    ${media.md.up} {
      height: ${($hasHeader) =>
        $hasHeader ? `calc(100% - ${topMenuHeight})` : '100%'};
      box-shadow: 0px 3px 6px ${shadow2};
      width: ${addToCartDialog.width};
      height: 100%;
      position: fixed;
      top: 0;
      bottom: 0;
      ${$side}: 0;
    }
  `}
`;

Wrapper.defaultProps = {
  $side: 'right',
};

export const HeaderWrapper = styled(Flex)<{
  $hasMobileVersion?: boolean;
  $isMobile?: boolean;
}>`
  position: relative;
  justify-content: space-between;
  align-items: center;

  display: ${({ $hasMobileVersion, $isMobile }) =>
    $hasMobileVersion ? ($isMobile ? 'flex' : 'none') : 'flex'};

  ${({ $hasMobileVersion, $isMobile, theme }) => css`
    border-bottom: 1px solid ${theme.colors.primaryAdd6};
    padding: ${theme.spacing(2, 2, 2, 2)};
    ${theme.media.sm.up} {
      display: ${$hasMobileVersion ? ($isMobile ? 'none' : 'flex') : 'flex'};
    }
  `}
`;

export const Header = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: center;
`;

export const CloseButton = styled.button`
  position: absolute !important;
  right: 1rem !important;
  all: unset;
  cursor: pointer;
  transform: translateX(5px);

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primaryBlue};
    outline-offset: 2px;
  }
`;

export const CloseIcon = styled(CloseSvg)`
  width: 24px;
  height: 24px;
  ${({ theme }) => css`
    fill: ${theme.colors.primaryBlue};
  `}
`;

export const Body = styled(Box)`
  flex: 1;
  overflow: auto;
  box-sizing: content-box;
  ${scrollbar}
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    width: 5px;
  }
`;

export const ProductsGrid = styled(Grid)`
  ${({ theme }) => css`
    padding: ${theme.spacing(0, 2)};
  `}
`;

export const AlreadyInBagText = styled(Typography)`
  width: 100%;
  text-align: center;
`;

export const Footer = styled(Box)`
  bottom: 0;
  justify-content: space-between;

  ${({ theme }) => css`
    background-color: ${theme.colors.white};
    padding: ${theme.spacing(1)};
    gap: ${theme.spacing(1)};

    ${theme.media.md.down} {
      left: 0;
      right: 0;
    }
  `}

  > a {
    width: 100%;
  }
`;

export const LinkButton = styled(LegacyButton)`
  gap: 0;
  svg {
    path {
      transition: color 0.3s;
    }
  }

  ${({ theme }) => css`
    margin-top: ${theme.spacing(2)};
    text-underline-offset: 0.25em;
    &:hover {
      svg {
        path {
          color: ${theme.colors.primary};
        }
      }
    }
  `}
`;

export const AlertMessage = styled(Flex)<{ type: 'warning' | 'info' }>`
  ${({ theme, type }) => css`
    background: #fff8e0;
    padding: ${theme.spacing(2)};

    svg {
      margin-right: ${theme.spacing(2)};
      path {
        ${type === 'warning'
          ? css`
              fill: ${theme.colors.accent};
            `
          : css`
              fill: ${theme.colors.secondaryGrey};
            `}
      }
    }
  `}
`;

export const Buttons = styled(Box)<{
  $isAddWarranty?: boolean;
}>`
  ${({ theme }) => css`
    & > button {
      border-bottom: 1px solid ${theme.colors.primaryAdd6};

      ${theme.media.md.up} {
        &:last-child {
          border-bottom: none;
          margin-bottom: 16px;
        }
      }
    }

    & > button {
      border-top: 1px solid ${theme.colors.primaryAdd6};
    }

    ${theme.media.md.down} {
      margin-inline: -${theme.spacing(2)};
    }

    ${theme.media.md.up} {
      padding-left: ${theme.spacing(0)};

      & > button {
        padding-left: 0;
      }
    }
  `}
`;
